import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { 
  getAllTechnicians, 
  addTechnician, 
  updateTechnician, 
  deleteTechnician,
  getTechniciansByBranch
} from '../services/technicianService';
import { getAllBranches } from '../services/branchService';
import { Technician, TechnicianSalaryType, Branch } from '../utils/types';

const TechniciansPage: React.FC = () => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState<Technician | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [technicianToDelete, setTechnicianToDelete] = useState<Technician | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // بيانات النموذج
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    specialization: '',
    salaryType: TechnicianSalaryType.FIXED,
    salaryAmount: 0,
    isActive: true,
    branchId: '',
    branchName: '',
    hireDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [techniciansData, branchesData] = await Promise.all([
        getAllTechnicians(),
        getAllBranches()
      ]);
      setTechnicians(techniciansData);
      setBranches(branchesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل البيانات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (technician?: Technician) => {
    if (technician) {
      setEditingTechnician(technician);
      setFormData({
        name: technician.name,
        phone: technician.phone,
        email: technician.email || '',
        specialization: technician.specialization,
        salaryType: technician.salaryType,
        salaryAmount: technician.salaryAmount,
        isActive: technician.isActive,
        branchId: technician.branchId,
        branchName: technician.branchName,
        hireDate: technician.hireDate instanceof Date 
          ? technician.hireDate.toISOString().split('T')[0]
          : new Date(technician.hireDate).toISOString().split('T')[0],
        notes: technician.notes || ''
      });
    } else {
      setEditingTechnician(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        specialization: '',
        salaryType: TechnicianSalaryType.FIXED,
        salaryAmount: 0,
        isActive: true,
        branchId: '',
        branchName: '',
        hireDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTechnician(null);
  };

  const handleBranchChange = (branchId: string) => {
    const selectedBranch = branches.find(b => b.id === branchId);
    setFormData({
      ...formData,
      branchId,
      branchName: selectedBranch?.name || ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.phone || !formData.branchId) {
        setSnackbar({
          open: true,
          message: 'يرجى ملء جميع الحقول المطلوبة',
          severity: 'error'
        });
        return;
      }

      if (editingTechnician) {
        await updateTechnician(editingTechnician.id, {
          ...formData,
          hireDate: new Date(formData.hireDate)
        });
        setSnackbar({
          open: true,
          message: 'تم تحديث بيانات الفني بنجاح',
          severity: 'success'
        });
      } else {
        await addTechnician({
          ...formData,
          hireDate: new Date(formData.hireDate)
        });
        setSnackbar({
          open: true,
          message: 'تم إضافة الفني بنجاح',
          severity: 'success'
        });
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving technician:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ بيانات الفني',
        severity: 'error'
      });
    }
  };

  const handleDeleteConfirm = (technician: Technician) => {
    setTechnicianToDelete(technician);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!technicianToDelete) return;

    try {
      await deleteTechnician(technicianToDelete.id);
      setSnackbar({
        open: true,
        message: 'تم حذف الفني بنجاح',
        severity: 'success'
      });
      setDeleteDialogOpen(false);
      setTechnicianToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting technician:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف الفني',
        severity: 'error'
      });
    }
  };

  const getSalaryTypeLabel = (type: TechnicianSalaryType): string => {
    switch (type) {
      case TechnicianSalaryType.FIXED:
        return 'راتب ثابت';
      case TechnicianSalaryType.PER_DEVICE:
        return 'بالقطعة';
      case TechnicianSalaryType.PERCENTAGE:
        return 'بالنسبة المئوية';
      case TechnicianSalaryType.VARIABLE:
        return 'قطعة متغيرة';
      default:
        return 'غير محدد';
    }
  };

  const getSalaryTypeColor = (type: TechnicianSalaryType): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (type) {
      case TechnicianSalaryType.FIXED:
        return 'primary';
      case TechnicianSalaryType.PER_DEVICE:
        return 'success';
      case TechnicianSalaryType.PERCENTAGE:
        return 'warning';
      case TechnicianSalaryType.VARIABLE:
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>جاري تحميل البيانات...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <PersonIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            إدارة الفنيين
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          إضافة فني جديد
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{technicians.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي الفنيين
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WorkIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {technicians.filter(t => t.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    الفنيين النشطين
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MoneyIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {technicians.filter(t => t.salaryType === TechnicianSalaryType.FIXED).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    راتب ثابت
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BusinessIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {new Set(technicians.map(t => t.branchId)).size}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    الفروع المغطاة
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Technicians Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            قائمة الفنيين
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>الاسم</TableCell>
                  <TableCell>الهاتف</TableCell>
                  <TableCell>التخصص</TableCell>
                  <TableCell>نوع الراتب</TableCell>
                  <TableCell>المبلغ</TableCell>
                  <TableCell>الفرع</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {technicians.map((technician) => (
                  <TableRow key={technician.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {technician.name}
                          </Typography>
                          {technician.email && (
                            <Typography variant="caption" color="text.secondary">
                              {technician.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                        {technician.phone}
                      </Box>
                    </TableCell>

                    <TableCell>{technician.specialization}</TableCell>

                    <TableCell>
                      <Chip
                        label={getSalaryTypeLabel(technician.salaryType)}
                        color={getSalaryTypeColor(technician.salaryType)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <MoneyIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                        {technician.salaryAmount}
                        {technician.salaryType === TechnicianSalaryType.PERCENTAGE ? '%' : ' جنيه'}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                        {technician.branchName}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={technician.isActive ? 'نشط' : 'غير نشط'}
                        color={technician.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Tooltip title="تعديل">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(technician)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteConfirm(technician)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTechnician ? 'تعديل بيانات الفني' : 'إضافة فني جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="اسم الفني *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="رقم الهاتف *"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="البريد الإلكتروني"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="التخصص *"
                value={formData.specialization}
                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                placeholder="مثل: هواتف ذكية، لابتوب، تابلت"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>نوع الراتب *</InputLabel>
                <Select
                  value={formData.salaryType}
                  onChange={(e) => setFormData({ ...formData, salaryType: e.target.value as TechnicianSalaryType })}
                  label="نوع الراتب *"
                >
                  <MenuItem value={TechnicianSalaryType.FIXED}>راتب ثابت</MenuItem>
                  <MenuItem value={TechnicianSalaryType.PER_DEVICE}>بالقطعة</MenuItem>
                  <MenuItem value={TechnicianSalaryType.PERCENTAGE}>بالنسبة المئوية</MenuItem>
                  <MenuItem value={TechnicianSalaryType.VARIABLE}>قطعة متغيرة</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={
                  formData.salaryType === TechnicianSalaryType.PERCENTAGE
                    ? "النسبة المئوية *"
                    : "المبلغ (جنيه) *"
                }
                type="number"
                value={formData.salaryAmount}
                onChange={(e) => setFormData({ ...formData, salaryAmount: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>الفرع *</InputLabel>
                <Select
                  value={formData.branchId}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  label="الفرع *"
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="تاريخ التوظيف"
                type="date"
                value={formData.hireDate}
                onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="فني نشط"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTechnician ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف الفني "{technicianToDelete?.name}"؟
            هذا الإجراء لا يمكن التراجع عنه.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TechniciansPage;
